/**
 * LiveInTheArchive — real recent archive activity (published/updated dossiers,
 * published media). No fabricated events; every row carries a genuine timestamp.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, FilePlus2, RefreshCw, Image as ImageIcon } from "lucide-react";
import { relTime, SectionHeader } from "./homeShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { published: FilePlus2, updated: RefreshCw, media: ImageIcon };

const Row = ({ e, i }) => {
  const Icon = ICONS[e.type] || RefreshCw;
  const thumb = e.thumb_url || e.image_url;
  const inner = (
    <>
      <div
        className="w-10 h-10 shrink-0 border overflow-hidden flex items-center justify-center"
        style={{ borderColor: "#2D2A26", background: "#0B0A0D" }}
      >
        {thumb ? (
          <img
            src={/^https?:\/\//i.test(thumb) ? thumb : `${process.env.REACT_APP_BACKEND_URL}${thumb}`}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover opacity-85 group-hover:opacity-100 transition-opacity"
            style={{ filter: "saturate(0.7)" }}
          />
        ) : (
          <Icon size={13} className={e.type === "published" ? "text-[#D92D20]" : "text-[#8A8377]"} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 font-mono text-[8px] uppercase tracking-[0.3em] text-[#8A8377]">
          <span className={e.type === "published" ? "text-[#D92D20]" : ""}>{e.label}</span>
          <span className="w-1 h-1 rounded-full bg-[#454038]" />
          <span>{relTime(e.at)}</span>
        </div>
        <div className="mt-1 text-[13px] leading-snug text-[#E4DFD3] group-hover:text-[#D92D20] transition-colors line-clamp-2">
          {e.title}
        </div>
      </div>
      {e.slug && (
        <span className="hidden sm:inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.25em] text-[#454038] group-hover:text-[#D92D20] transition-colors shrink-0">
          View <ArrowRight size={10} />
        </span>
      )}
    </>
  );
  const cls = "flex items-center gap-3 py-3 px-3 border-b last:border-b-0 group transition-colors";
  const style = { borderColor: "#2D2A26" };
  return e.slug ? (
    <Link to={`/topic/${e.slug}`} data-testid={`activity-row-${i}`} className={`${cls} hover:bg-[#131015]`} style={style}>
      {inner}
    </Link>
  ) : (
    <div data-testid={`activity-row-${i}`} className={cls} style={style}>{inner}</div>
  );
};

const LiveInTheArchive = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let dead = false;
    axios.get(`${API}/home/activity`, { timeout: 12000 })
      .then((r) => { if (!dead) setItems(r.data?.items || []); })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  if (!items.length) return null;
  return (
    <section data-testid="home-live-activity" className="border-t" style={{ borderColor: "#2D2A26", background: "#08070A" }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-20">
        <SectionHeader
          file="File 003 · Operations Log"
          title="Live in the Archive"
          subtitle="Real activity from the record — as dossiers, evidence and media enter the Archive."
        />
        <div className="border relative overflow-hidden" style={{ borderColor: "#2D2A26", background: "#0F0E12" }}>
          <div
            className="flex items-center gap-2.5 px-3 py-2.5 border-b"
            style={{ borderColor: "#2D2A26" }}
          >
            <span className="relative flex w-2 h-2">
              <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D92D20] opacity-60" />
              <span className="relative inline-flex rounded-full w-2 h-2 bg-[#D92D20]" />
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#A39E93]">
              Live update feed
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 lg:[&>*:nth-child(odd)]:border-r" style={{ "--tw-border-opacity": 1 }}>
            {items.slice(0, 8).map((e, i) => <Row key={`${e.type}-${e.slug || i}-${e.at}`} e={e} i={i} />)}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveInTheArchive;
